import type { A11yTextType } from '../../../common/components/A11yTextWrapper/A11yTextWrapper';
import type {
    EdlpEcomPromotion,
    FixedEcomPromotion,
    MultiBuyEcomPromotion,
    PercentageEcomPromotion,
} from '../../../common/models/domain/ecommerce/promotion/promotion';
import type { PriceData, PriceType } from '../../../common/models/priceData/priceData';

import { Helpers } from '../../../../utility/helpers';
import { getEdlpPrimaryPriceA11yText } from '../EdlpPricingSplash';
import { ProductPriceLabel } from '../ProductPriceLabel';
import { useSyncA11yTextWithParent } from './EcomProductPrice.hooks';
import styles from './EcomProductPrice.module.scss';

type BaseEcomProductPriceProps = {
    priceType: PriceType;

    a11yType: A11yTextType;
    setA11yLabel?: (label: string) => void;
};

const getEcomProductPercentagePromotionPriceTexts = ({
    isMemberPrice,
    percentage,
    newPiecePrice,
    priceType,
}: {
    isMemberPrice: boolean;
    percentage: number;
    newPiecePrice: PriceData | undefined;
    priceType: PriceType;
}) => {
    const texts = newPiecePrice && getPriceTexts(newPiecePrice, priceType);

    const a11yText = [
        isMemberPrice ? 'Medlemspris' : 'Erbjudande',
        `${percentage}% rabatt,`,
        texts?.a11y,
        texts?.a11y && getPerUnitText('st'),
    ]
        .filter(Boolean)
        .join(' ');

    return {
        a11yText,
        visualText: texts?.visual,
    };
};

const EcomProductPercentagePromotionPrice = (
    props: {
        promo: PercentageEcomPromotion;
        newPiecePrice?: PriceData;
        percentage: number;
    } & BaseEcomProductPriceProps,
) => {
    const { a11yText } = getEcomProductPercentagePromotionPriceTexts({
        isMemberPrice: props.promo.isMemberPrice,
        percentage: props.percentage,
        newPiecePrice: props.newPiecePrice,
        priceType: props.priceType,
    });

    useSyncA11yTextWithParent(a11yText, props.setA11yLabel);

    return (
        <div className={styles.Container}>
            <ProductPriceLabel.MaybeMemberDiscount
                isMemberPrice={props.promo.isMemberPrice}
                a11yType={props.a11yType}
                a11yText={a11yText}
                secondPrice={
                    props.newPiecePrice && (
                        <EcomProductFlatDiscountPrice
                            newPiecePrice={props.newPiecePrice}
                            priceType={props.priceType}
                            a11yType="ariaHidden" // text already handled in variable above
                        />
                    )
                }
            >
                {props.percentage}%
            </ProductPriceLabel.MaybeMemberDiscount>
        </div>
    );
};

const getEcomProductFixedPromotionPriceTexts = (
    promo: FixedEcomPromotion,
    priceType: PriceType,
) => {
    // Price per unit, i.e. per kilo
    const priceTexts = getPriceTexts(promo.fixedPrice, priceType);

    // Price per piece
    const piecePriceTexts =
        promo.fixedPiecePrice && getPriceTexts(promo.fixedPiecePrice, priceType);

    const promoIsPerKilo = promo.unit === 'kg';

    const a11yText = priceTexts
        ? [
              promo.isMemberPrice ? 'Medlemspris' : 'Erbjudande',
              promoIsPerKilo && `${priceTexts.a11y} ${getPerUnitText('kg')},`,
              piecePriceTexts?.a11y && `${piecePriceTexts.a11y} ${getPerUnitText('st')}`,
          ]
              .filter(Boolean)
              .join(' ')
        : undefined;

    return {
        a11yText,
        visualPriceText: priceTexts?.visual,
        visualPiecePriceTexts: piecePriceTexts?.visual,
    };
};

const EcomProductFixedPromotionPrice = (
    props: {
        promo: FixedEcomPromotion;
    } & BaseEcomProductPriceProps,
) => {
    const promoIsPerKilo = props.promo.unit === 'kg';

    const { a11yText, visualPiecePriceTexts, visualPriceText } =
        getEcomProductFixedPromotionPriceTexts(props.promo, props.priceType);

    useSyncA11yTextWithParent(a11yText, props.setA11yLabel);

    if (promoIsPerKilo) {
        const piecePriceTag = visualPiecePriceTexts ? (
            <ProductPriceLabel.Discount
                currency
                unit="st"
                a11yText={undefined} // text already handled in variable above
                a11yType="ariaHidden"
            >
                {visualPiecePriceTexts}
            </ProductPriceLabel.Discount>
        ) : undefined;

        return (
            <div className={styles.Container}>
                {visualPriceText && (
                    <ProductPriceLabel.MaybeMemberDiscount
                        isMemberPrice={props.promo.isMemberPrice}
                        currency
                        unit="kg"
                        a11yType={props.a11yType}
                        a11yText={a11yText}
                        secondPrice={piecePriceTag}
                    >
                        {visualPriceText}
                    </ProductPriceLabel.MaybeMemberDiscount>
                )}
                {visualPiecePriceTexts && !visualPriceText && (
                    <ProductPriceLabel.MaybeMemberDiscount
                        isMemberPrice={props.promo.isMemberPrice}
                        currency
                        unit="st"
                        a11yType={props.a11yType}
                        a11yText={a11yText}
                    >
                        {visualPiecePriceTexts}
                    </ProductPriceLabel.MaybeMemberDiscount>
                )}
            </div>
        );
    }

    return (
        visualPiecePriceTexts && (
            <ProductPriceLabel.MaybeMemberDiscount
                isMemberPrice={props.promo.isMemberPrice}
                currency
                unit="st"
                a11yType={props.a11yType}
                a11yText={a11yText}
            >
                {visualPiecePriceTexts}
            </ProductPriceLabel.MaybeMemberDiscount>
        )
    );
};

const getEcomProductMultiBuyPromotionPriceTexts = (
    promo: MultiBuyEcomPromotion,
    priceType: PriceType,
) => {
    const texts = getPriceTexts(promo.bundlePrice, priceType);

    const a11yText =
        texts?.a11y &&
        [
            promo.isMemberPrice ? 'Medlemspris' : 'Erbjudande',
            `${promo.numberOfProductRequired} för`,
            texts.a11y,
        ]
            .filter(Boolean)
            .join(' ');

    return {
        a11yText,
        visualText: texts?.visual,
    };
};

const EcomProductMultiBuyPromotionPrice = (
    props: {
        promo: MultiBuyEcomPromotion;
    } & BaseEcomProductPriceProps,
) => {
    const { a11yText, visualText } = getEcomProductMultiBuyPromotionPriceTexts(
        props.promo,
        props.priceType,
    );

    useSyncA11yTextWithParent(a11yText, props.setA11yLabel);

    if (!visualText) return null;

    return (
        <ProductPriceLabel.MaybeMemberDiscount
            isMemberPrice={props.promo.isMemberPrice}
            currency
            a11yType={props.a11yType}
            a11yText={a11yText}
        >
            <>
                <span>{props.promo.numberOfProductRequired}</span>
                <span>för</span>
                {visualText}
            </>
        </ProductPriceLabel.MaybeMemberDiscount>
    );
};

const getEcomProductFlatDiscountPriceTexts = (newPiecePrice: PriceData, priceType: PriceType) => {
    const texts = getPriceTexts(newPiecePrice, priceType);

    const a11yText = texts?.a11y ? `Erbjudande ${texts.a11y} ${getPerUnitText('st')}` : undefined;

    return {
        a11yText,
        visualText: texts?.visual,
    };
};

const EcomProductFlatDiscountPrice = (
    props: {
        newPiecePrice: PriceData;
    } & BaseEcomProductPriceProps,
) => {
    const { a11yText, visualText } = getEcomProductFlatDiscountPriceTexts(
        props.newPiecePrice,
        props.priceType,
    );

    useSyncA11yTextWithParent(a11yText, props.setA11yLabel);

    if (!visualText) return null;

    return (
        <ProductPriceLabel.Discount
            currency
            unit="st"
            a11yType={props.a11yType}
            a11yText={a11yText}
        >
            {visualText}
        </ProductPriceLabel.Discount>
    );
};

const getEcomProductRegularPriceTexts = (
    piecePrice: PriceData | undefined,
    priceType: PriceType,
    displayLowerPrice: boolean,
) => {
    const texts = piecePrice && getPriceTexts(piecePrice, priceType);

    const a11yText =
        texts?.a11y &&
        `${displayLowerPrice ? 'Lägre pris' : 'Pris'} ${texts.a11y} ${getPerUnitText('st')}`;

    return {
        a11yText,
        visualText: texts?.visual,
    };
};

/**
 * EDLP primary looks like multi-buy ("X för Y") using bundlePrice + numberOfProductRequired,
 * but stays on regular/ghost (blackish) chrome — never campaign attention/red.
 */
const getEcomProductEdlpPriceTexts = (promo: EdlpEcomPromotion, priceType: PriceType) => {
    const texts = getPriceTexts(promo.bundlePrice, priceType);

    const a11yText =
        texts?.a11y &&
        getEdlpPrimaryPriceA11yText(`${promo.numberOfProductRequired} för ${texts.a11y}`);

    return {
        a11yText,
        visualText: texts?.visual,
        numberOfProductRequired: promo.numberOfProductRequired,
    };
};

const EcomProductRegularPrice = (
    props: {
        displayLowerPrice: boolean;
        piecePrice?: PriceData;
    } & BaseEcomProductPriceProps,
) => {
    const { a11yText, visualText } = getEcomProductRegularPriceTexts(
        props.piecePrice,
        props.priceType,
        props.displayLowerPrice,
    );

    useSyncA11yTextWithParent(a11yText, props.setA11yLabel);

    if (!visualText) return null;

    return (
        <ProductPriceLabel.MaybeLowerPrice
            currency
            unit="st"
            a11yType={props.a11yType}
            a11yText={a11yText}
            displayLowerPrice={props.displayLowerPrice}
        >
            {visualText}
        </ProductPriceLabel.MaybeLowerPrice>
    );
};

const EcomProductEdlpPrice = (
    props: {
        promo: EdlpEcomPromotion;
    } & BaseEcomProductPriceProps,
) => {
    const { a11yText, visualText, numberOfProductRequired } = getEcomProductEdlpPriceTexts(
        props.promo,
        props.priceType,
    );

    useSyncA11yTextWithParent(a11yText, props.setA11yLabel);

    if (!visualText) return null;

    return (
        <ProductPriceLabel.Regular currency a11yType={props.a11yType} a11yText={a11yText}>
            <>
                <span>{numberOfProductRequired}</span>
                <span>för</span>
                {visualText}
            </>
        </ProductPriceLabel.Regular>
    );
};

const EcomProductPrice = {
    Regular: EcomProductRegularPrice,
    Edlp: EcomProductEdlpPrice,
    FlatDiscount: EcomProductFlatDiscountPrice,
    PercentagePromotion: EcomProductPercentagePromotionPrice,
    FixedPromotion: EcomProductFixedPromotionPrice,
    MultiBuyPromotion: EcomProductMultiBuyPromotionPrice,
    getFixedPromotionTexts: getEcomProductFixedPromotionPriceTexts,
    getRegularPriceTexts: getEcomProductRegularPriceTexts,
    getEdlpPriceTexts: getEcomProductEdlpPriceTexts,
    getFlatDiscountPriceTexts: getEcomProductFlatDiscountPriceTexts,
    getPercentagePromotionTexts: getEcomProductPercentagePromotionPriceTexts,
    getMultiBuyPromotionTexts: getEcomProductMultiBuyPromotionPriceTexts,
};

export default EcomProductPrice;

const getPriceTexts = (priceData: PriceData, priceType: PriceType) => {
    const price = Helpers.getPrice(priceData, priceType);

    if (!price) return null;

    return {
        visual: Helpers.toEcommercePrice(price, 'hideRest'),
        a11y: Helpers.toEcommercePriceWithCurrencyAriaLabel(price),
    };
};

const getPerUnitText = (unit: 'st' | 'kg') => {
    if (unit === 'st') {
        return 'styck';
    }

    if (unit === 'kg') {
        return 'per kilo';
    }
};
